import { Calendar } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { es } from 'date-fns/locale';
import { format, parse, startOfDay } from 'date-fns';
import 'react-day-picker/style.css';

function parseLocalYmd(s) {
  if (!s) return undefined;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * Selector de un día (react-day-picker) que sustituye a <input type="date">,
 * para poder deshabilitar días concretos (días cerrados). Valor en yyyy-MM-dd.
 */
export default function SingleDatePicker({
  id,
  value,
  onChange,
  /** Deshabilita días anteriores a esta fecha (yyyy-MM-dd o Date). */
  minDate = null,
  /** Días cerrados (yyyy-MM-dd): se muestran tachados y no se pueden elegir. */
  closedDates = null,
  disabled = false,
  hasError = false,
  className = 'date-input',
  placeholder = 'dd/mm/aaaa',
  locale = es,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = parseLocalYmd(value);

  const closedDayDates = useMemo(
    () => (closedDates || []).map((d) => parseLocalYmd(d)).filter(Boolean),
    [closedDates]
  );

  const minDay = minDate ? startOfDay(typeof minDate === 'string' ? parseLocalYmd(minDate) : minDate) : null;
  const disabledMatchers = [...(minDay ? [{ before: minDay }] : []), ...closedDayDates];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const root = containerRef.current;
      if (root && !root.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <div className="single-date-picker date-input-container" ref={containerRef}>
      <button
        id={id}
        type="button"
        className={`${className} single-date-trigger ${hasError ? 'error' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {selected ? format(selected, 'dd/MM/yyyy') : <span className="single-date-placeholder">{placeholder}</span>}
      </button>
      <Calendar size={20} className="single-date-icon" aria-hidden />
      {open && (
        <div className="date-range-dropdown single-date-dropdown" role="dialog">
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (!d) return;
              onChange(format(d, 'yyyy-MM-dd'));
              setOpen(false);
            }}
            locale={locale}
            defaultMonth={selected ?? minDay ?? new Date()}
            weekStartsOn={1}
            disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
            modifiers={{ closed: closedDayDates }}
            modifiersClassNames={{ closed: 'date-range-day-closed' }}
          />
        </div>
      )}
    </div>
  );
}
